import { Emitter } from "../../../base/common/events.js";
import { Disposable } from "../../../base/common/lifecycle.js";
import { IHierarchyService } from "../../hierarchy/common/hierarchy.js";
import { IInspectionService, InspectRequest, InspectionTargetRef, InspectionTargetsChange } from "./inspection.js";

export class InspectionService extends Disposable implements IInspectionService {

    readonly _serviceBrand: undefined;

    private readonly _onDidRequestInspection = this._register(new Emitter<InspectRequest | null>());
    readonly onDidRequestInspection = this._onDidRequestInspection.event;

    private readonly _onDidTargetsChange = this._register(new Emitter<InspectionTargetsChange>());
    readonly onDidTargetsChange = this._onDidTargetsChange.event;

    // weak: an inspection request must never extend the target's lifetime
    private _current: { readonly ref: WeakRef<object>; readonly source: string } | undefined;

    /** Last "reveal" request (weak) — see {@link IInspectionService.getLastReveal}. */
    private _lastReveal: { readonly ref: WeakRef<object>; readonly source: string } | undefined;

    /** Selection-derived base (weak; the hierarchy retains the owners anyway). */
    private _selection: WeakRef<object>[] = [];

    /**
     * Override layers, insertion-ordered — the LAST one wins. Refs are held
     * strongly (their payloads capture live objects anyway); lifetime is
     * bounded: any base change clears them, and the pushing panel removes its
     * layers on teardown. See {@link IInspectionService.setOverride}.
     */
    private _overrides: { source: string; refs: readonly InspectionTargetRef[] }[] = [];

    constructor(
        // Optional so the service also constructs bare (unit tests, products
        // without a hierarchy) — then only inspect()/overrides drive the base.
        @IHierarchyService private readonly hierarchy?: IHierarchyService,
    ) {
        super();
        if (this.hierarchy) {
            // A REAL selection change resets the base — it drops a pending
            // inspect target and all overrides (navigating away abandons the
            // drill-in). But the hierarchy RE-EMITS selection events with the
            // same logical selection (node restoration while a live scene
            // mutates, the force-refresh hack after add-component) — those must
            // NOT clobber an inspected resource (a Resources-panel pick would
            // silently fall back to the selection within moments on any
            // animated page). Same-selection events still fire the change so
            // consumers re-collect (refresh semantics).
            this._register(this.hierarchy.onSelectedNodesDidChange((e) => {
                this._setSelection(e.selection?.map((n) => n.owner) ?? []);
            }));
            this._setSelection(this.hierarchy.getSelectedNodes()?.map((n) => n.owner) ?? [], /* silent */ true);
        }
    }

    // #region inspect / clear (base)

    getCurrentTarget(): InspectRequest | undefined {
        if (!this._current) return undefined;
        const target = this._current.ref.deref();
        if (!target) {
            this._current = undefined;
            return undefined;
        }
        return { target, source: this._current.source };
    }

    inspect(request: InspectRequest): void {
        if (!request?.target || typeof request.target !== "object") return;
        // A reveal shows the object in the Inspector (the request event) but
        // leaves the inspection base and overrides alone — target-following
        // panels keep their context (see InspectRequest.effect). It IS
        // remembered as the user's latest pointing gesture for the AI.
        if (request.effect === "reveal") {
            this._lastReveal = { ref: new WeakRef(request.target), source: request.source };
            this._onDidRequestInspection.fire(request);
            return;
        }
        this._current = { ref: new WeakRef(request.target), source: request.source };
        this._overrides = [];
        this._lastReveal = undefined; // a real navigation supersedes the reveal
        this._onDidRequestInspection.fire(request);
        this._onDidTargetsChange.fire({ baseChanged: true });
    }

    getLastReveal(): InspectRequest | undefined {
        if (!this._lastReveal) return undefined;
        const target = this._lastReveal.ref.deref();
        if (!target) {
            this._lastReveal = undefined;
            return undefined;
        }
        return { target, source: this._lastReveal.source, effect: "reveal" };
    }

    clear(): void {
        if (!this._current) return;
        this._current = undefined;
        this._lastReveal = undefined;
        this._onDidRequestInspection.fire(null);
        this._onDidTargetsChange.fire({ baseChanged: true });
    }

    private _setSelection(owners: object[], silent = false): void {
        const next = owners.filter((o) => !!o && typeof o === "object");
        // Same owners as before = a re-emission, not a navigation: keep the
        // inspect target and overrides (see the constructor comment).
        const previous = this._selection.map((ref) => ref.deref());
        const changed = next.length !== previous.length || next.some((o, i) => previous[i] !== o);
        this._selection = next.map((o) => new WeakRef(o));
        if (changed) {
            this._current = undefined; // a real selection supersedes an inspect request
            this._overrides = [];
            this._lastReveal = undefined;
        }
        if (!silent) this._onDidTargetsChange.fire({ baseChanged: true });
    }

    // #endregion

    // #region target layers

    get hasOverride(): boolean {
        return this._overrides.length > 0;
    }

    getTargets(layer: "effective" | "base" = "effective"): InspectionTargetRef[] {
        if (layer === "effective" && this._overrides.length) {
            return [...this._overrides[this._overrides.length - 1].refs];
        }
        // Base: the pending inspect target, else the selection owners.
        const inspected = this.getCurrentTarget();
        if (inspected) return [{ target: inspected.target, label: undefined }];
        const out: InspectionTargetRef[] = [];
        for (const ref of this._selection) {
            const target = ref.deref();
            if (target) out.push({ target });
        }
        return out;
    }

    getFocusTargets(): InspectionTargetRef[] {
        // A live reveal is the user's freshest pointing gesture (a real base or
        // selection navigation would have cleared it), so it wins for the
        // resource-oriented read tools — leaving the base (code panel / code_read)
        // untouched.
        const reveal = this.getLastReveal();
        if (reveal) return [{ target: reveal.target }];
        return this.getTargets("effective");
    }

    setOverride(source: string, refs: readonly InspectionTargetRef[] | null): void {
        const valid = refs?.filter((r) => !!r?.target && typeof r.target === "object") ?? [];
        const index = this._overrides.findIndex((o) => o.source === source);
        if (!valid.length) {
            if (index < 0) return; // nothing to remove
            this._overrides.splice(index, 1);
        } else if (index >= 0) {
            // Same targets re-set (e.g. re-clicking the same node) → no-op. Judged
            // by target identity + label, NOT payload identity: callers rebuild
            // payload objects per call, but for a given target they're equivalent.
            const prev = this._overrides[index].refs;
            if (prev.length === valid.length && prev.every((r, i) =>
                r.target === valid[i].target && r.label === valid[i].label)) return;
            // Replace in place — the layer keeps its stack position.
            this._overrides[index] = { source, refs: valid };
        } else {
            this._overrides.push({ source, refs: valid });
        }
        this._onDidTargetsChange.fire({ baseChanged: false });
    }

    clearOverrides(): void {
        if (!this._overrides.length) return;
        this._overrides = [];
        this._onDidTargetsChange.fire({ baseChanged: false });
    }

    // #endregion
}
